import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { trackEvent } from "../../utils/analytics";
import * as Sentry from "@sentry/react";
import { AIError } from "../../errors/sentryErrors";

const formatDateDisplay = (dateStr) => {
  if (!dateStr) return "";
  const parts = dateStr.split("-");
  if (parts.length === 3) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return dateStr;
};

const Step3Form = ({ formData, setFormData }) => {
  const [currencies, setCurrencies] = useState(["INR", "USD", "EUR", "GBP"]);
  const [isRoomDropdownOpen, setIsRoomDropdownOpen] = useState(false);
  const [activeRoomIndex, setActiveRoomIndex] = useState(0);
  const dropdownRef = useRef(null);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsRoomDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getBudgetRange = (budget) => {
    const amount = Number(budget);

    if (amount < 25000) return "Under 25K";
    if (amount < 50000) return "25K–50K";
    if (amount < 100000) return "50K–1L";
    if (amount < 250000) return "1L–2.5L";
    if (amount < 500000) return "2.5L–5L";
    if (amount < 1000000) return "5L–10L";

    return "10L+";
  };
  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const apiKey = import.meta.env.VITE_FREECURRENCY_API_KEY;
        const res = await fetch(
          `https://api.freecurrencyapi.com/v1/latest?apikey=${apiKey}`,
        );

        if (!res.ok) {
          throw new Error("Failed to fetch currencies");
        }

        const json = await res.json();
        if (json && json.data) {
          // Sort currencies alphabetically, putting INR, USD, EUR, GBP at the top
          const fetchedCurrencies = Object.keys(json.data).sort();
          const priority = ["INR", "USD", "EUR", "GBP"];
          const sorted = [
            ...priority.filter((c) => fetchedCurrencies.includes(c)),
            ...fetchedCurrencies.filter((c) => !priority.includes(c)),
          ];
          setCurrencies(sorted.length > 0 ? sorted : fetchedCurrencies);
        }
      } catch (err) {
        Sentry.withScope((scope) => {
          scope.setTag("feature", "ai");
          scope.setTag("app", "web");
          scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
          Sentry.captureException(new AIError(err instanceof Error ? err.message : String(err)));
        });
        console.error("Failed to fetch currencies", err);
      }
    };
    fetchCurrencies();
  }, []);

  const numberOfNights =
    formData.startDate && formData.endDate
      ? Math.max(
        0,
        Math.ceil(
          (new Date(formData.endDate) - new Date(formData.startDate)) /
          (1000 * 60 * 60 * 24),
        ),
      )
      : 0;

  const durationDisplay =
    numberOfNights > 0
      ? `${numberOfNights} Night${numberOfNights !== 1 ? "s" : ""} / ${numberOfNights + 1} Day${numberOfNights + 1 !== 1 ? "s" : ""}`
      : "-";

  const rooms = formData.rooms || [{ adults: 2, children: [] }];

  const hasUnselectedChildAges = rooms.some(room => 
    room.children?.some(child => {
      const age = child && typeof child === "object" ? child.age : child;
      return age === "" || age === undefined || age === null;
    })
  );

  const handleUpdateAdults = (index, increment) => {
    const newRooms = [...rooms];
    const currentAdults = newRooms[index].adults;
    const currentChildrenCount = (newRooms[index].children || []).length;

    let newAdults = currentAdults + increment;
    newAdults = Math.max(0, Math.min(12, newAdults)); // Limit adults 0-12

    // Max 16 pax per room rule (12 adults + 4 children)
    if (newAdults + currentChildrenCount > 16) {
      return; // Cannot exceed 16 pax in total
    }

    newRooms[index] = { ...newRooms[index], adults: newAdults };
    setFormData({ ...formData, rooms: newRooms });
    trackEvent("traveler_count_updated", {
      room_index: index + 1,
      room_adults: newAdults,
    });
  };

  const handleAddChild = (index) => {
    const newRooms = [...rooms];
    const currentAdults = newRooms[index].adults;
    const currentChildren = newRooms[index].children || [];

    if (currentChildren.length >= 4) {
      return; // Cannot exceed 4 kids
    }

    // Max 16 pax per room rule (12 adults + 4 children)
    if (currentAdults + currentChildren.length >= 16) {
      return; // Cannot exceed 16 pax in total
    }

    newRooms[index] = {
      ...newRooms[index],
      children: [...currentChildren, { age: "" }], // Start with empty age object
    };
    setFormData({ ...formData, rooms: newRooms });
    trackEvent("child_added", { room_index: index + 1 });

    // Auto-scroll down to show the new child age field
    setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
      }
    }, 100);
  };

  const handleUpdateChildAge = (roomIndex, childIndex, age) => {
    const newRooms = [...rooms];
    const newChildren = [...(newRooms[roomIndex].children || [])];

    let parsedAge = parseInt(age, 10);
    if (age === "") parsedAge = "";
    else if (isNaN(parsedAge)) parsedAge = 0;
    else if (parsedAge < 0) parsedAge = 0;
    else if (parsedAge > 17) parsedAge = 17;

    newChildren[childIndex] = { age: parsedAge };
    newRooms[roomIndex] = { ...newRooms[roomIndex], children: newChildren };
    setFormData({ ...formData, rooms: newRooms });
  };

  const handleRemoveChild = (roomIndex, childIndex) => {
    const newRooms = [...rooms];
    const newChildren = (newRooms[roomIndex].children || []).filter(
      (_, i) => i !== childIndex,
    );
    newRooms[roomIndex] = { ...newRooms[roomIndex], children: newChildren };
    setFormData({ ...formData, rooms: newRooms });
    trackEvent("child_removed", { room_index: roomIndex + 1 });
  };

  const handleAddRoom = () => {
    if (hasUnselectedChildAges) {
      // Guidance, not a failure — the user just has a step left to complete.
      toast.warning("Please select ages for all children before adding a new room.");
      return;
    }
    if (rooms.length >= 6) return;
    const newRooms = [...rooms, { adults: 2, children: [] }];
    setFormData({ ...formData, rooms: newRooms });
    setActiveRoomIndex(newRooms.length - 1);
    trackEvent("room_added", { total_rooms: newRooms.length });
  };

  const handleRemoveRoom = (index) => {
    if (rooms.length <= 1) return;
    const newRooms = rooms.filter((_, i) => i !== index);
    setFormData({ ...formData, rooms: newRooms });
    if (activeRoomIndex === index) {
      setActiveRoomIndex(Math.max(0, index - 1));
    } else if (activeRoomIndex > index) {
      setActiveRoomIndex(activeRoomIndex - 1);
    }
    trackEvent("room_removed", { total_rooms: newRooms.length });
  };

  // --- Pax Classification Logic ---
  const paxDetails = {
    adults: 0,
    adultExtraBeds: 0,
    childrenWithBed: 0,
    childrenNoBed: 0,
    infants: 0,
    totalPax: 0,
    warnings: [],
  };

  if (rooms.length === 0) {
    paxDetails.warnings.push("Add at least one room.");
  }

  rooms.forEach((room, i) => {
    const roomNum = i + 1;
    let standardAdultSeatsUsed = 0;

    if (room.adults === 0) {
      paxDetails.warnings.push(
        `Room ${roomNum} has no adults — most hotels require at least 1 adult per room.`,
      );
    }

    for (let j = 0; j < room.adults; j++) {
      if (standardAdultSeatsUsed < 2) {
        paxDetails.adults++;
        standardAdultSeatsUsed++;
      } else {
        paxDetails.adultExtraBeds++;
      }
    }

    const childrenAges = room.children || [];
    childrenAges.forEach((child) => {
      const age = child && typeof child === "object" ? child.age : child;
      if (age === "" || age === undefined || age === null || isNaN(age)) {
        paxDetails.warnings.push(`Room ${roomNum} has an invalid child age.`);
        return;
      }

      const numAge = Number(age);
      if (numAge < 2) {
        paxDetails.infants++;
      } else if (numAge >= 2 && numAge <= 5) {
        paxDetails.childrenNoBed++;
      } else if (numAge >= 6 && numAge <= 12) {
        paxDetails.childrenWithBed++;
      } else if (numAge >= 13) {
        if (standardAdultSeatsUsed < 2) {
          paxDetails.adults++;
          standardAdultSeatsUsed++;
        } else {
          paxDetails.adultExtraBeds++;
        }
      }
    });
  });

  paxDetails.totalPax =
    paxDetails.adults +
    paxDetails.adultExtraBeds +
    paxDetails.childrenWithBed +
    paxDetails.childrenNoBed +
    paxDetails.infants;

  // Calculate totals for Trip Summary
  const totalChildren = paxDetails.childrenWithBed + paxDetails.childrenNoBed;
  const totalInfants = paxDetails.infants;

  return (
    <div className="space-y-8">
      <div className="relative" ref={dropdownRef}>
        <div className="mb-3">
          <label className="block text-sm font-semibold text-gray-900 mb-1">
            Rooms & Guests
          </label>
        </div>

        {/* Trigger Button */}
        <button
          type="button"
          onClick={() => setIsRoomDropdownOpen(!isRoomDropdownOpen)}
          className="w-full sm:w-[340px] flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl hover:border-teal-300 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal-500/20"
        >
          <div className="flex items-baseline gap-1.5 text-gray-900">
            <span className="text-2xl font-bold">{rooms.length}</span>
            <span className="text-sm font-medium">Room</span>
            <span className="text-2xl font-bold ml-2">{paxDetails.totalPax}</span>
            <span className="text-sm font-medium">Guests</span>
          </div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-5 w-5 text-gray-500 transition-transform ${
              isRoomDropdownOpen ? "rotate-180" : ""
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Dropdown Popover */}
        {isRoomDropdownOpen && (
          <div className="absolute z-10 top-[85px] left-0 w-full sm:w-[380px] bg-white border border-gray-200 rounded-2xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)] overflow-hidden flex flex-col font-sans">
            <div ref={scrollContainerRef} className="max-h-[400px] overflow-y-auto p-5 space-y-2">
              {rooms.map((room, index) => {
                const isActive = activeRoomIndex === index;
                return (
                  <div key={index} className={index > 0 ? "pt-4 border-t border-gray-100" : ""}>
                    <div 
                      className="flex justify-between items-start mb-2 cursor-pointer group"
                      onClick={() => setActiveRoomIndex(isActive ? -1 : index)}
                    >
                      <div>
                        <h4 className="font-bold text-gray-900 text-lg flex items-center gap-2 group-hover:text-teal-700 transition-colors">
                          Room {index + 1}
                          <svg className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${isActive ? 'rotate-180 text-teal-600' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </h4>
                        {!isActive && (
                          <p className="text-sm text-gray-500 mt-1">
                            {room.adults} Adults, {room.children?.length || 0} Children
                          </p>
                        )}
                      </div>

                      {rooms.length > 1 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveRoom(index);
                          }}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1.5 rounded-md transition-colors cursor-pointer mt-0.5"
                          title="Remove Room"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                          </svg>
                        </button>
                      )}
                    </div>

                    {isActive && (
                      <div className="space-y-5 pt-3 pb-2 animate-in fade-in slide-in-from-top-2 duration-200">
                        {/* Adults */}
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="block text-gray-900 text-base font-medium">Adults</span>
                            <span className="block text-gray-500 text-sm mt-0.5">Age 13 and above</span>
                          </div>
                          <div className="flex items-center rounded-full border border-gray-300 bg-white shadow-sm overflow-hidden">
                            <button
                              type="button"
                              onClick={() => handleUpdateAdults(index, -1)}
                              disabled={room.adults <= 0}
                              className="w-10 h-10 flex items-center justify-center text-gray-700 hover:bg-gray-50 hover:text-teal-600 disabled:opacity-30 cursor-pointer font-medium text-xl"
                            >
                              −
                            </button>
                            <span className="w-10 text-center text-base font-bold text-gray-900 bg-gray-200 h-10 leading-[40px]">
                              {room.adults}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleUpdateAdults(index, 1)}
                              disabled={room.adults >= 12}
                              className="w-10 h-10 flex items-center justify-center text-gray-700 hover:bg-gray-50 hover:text-teal-600 disabled:opacity-30 cursor-pointer font-medium text-xl"
                            >
                              +
                            </button>
                          </div>
                        </div>

                        {/* Children */}
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="block text-gray-900 text-base font-medium">Children</span>
                            <span className="block text-gray-500 text-sm mt-0.5">Age Below 13</span>
                          </div>
                          <div className="flex items-center rounded-full border border-gray-300 bg-white shadow-sm overflow-hidden">
                            <button
                              type="button"
                              onClick={() => handleRemoveChild(index, (room.children?.length || 1) - 1)}
                              disabled={(room.children?.length || 0) <= 0}
                              className="w-10 h-10 flex items-center justify-center text-gray-700 hover:bg-gray-50 hover:text-teal-600 disabled:opacity-30 cursor-pointer font-medium text-xl"
                            >
                              −
                            </button>
                            <span className="w-10 text-center text-base font-bold text-gray-900 bg-gray-200 h-10 leading-[40px]">
                              {room.children?.length || 0}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleAddChild(index)}
                              disabled={(room.children?.length || 0) >= 4}
                              className="w-10 h-10 flex items-center justify-center text-gray-700 hover:bg-gray-50 hover:text-teal-600 disabled:opacity-30 cursor-pointer font-medium text-xl"
                            >
                              +
                            </button>
                          </div>
                        </div>

                        {/* Children Age Selectors */}
                        {room.children && room.children.length > 0 && (
                          <div className="space-y-3 mt-4 pt-4 border-t border-gray-100">
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-4">
                              {room.children.map((child, childIndex) => {
                                const age = child && typeof child === "object" ? child.age : child;
                                return (
                                  <div key={childIndex} className="flex flex-col">
                                    <label className="text-xs font-medium text-red-500 mb-1.5">
                                      Child {childIndex + 1} Age
                                    </label>
                                    <div className="relative">
                                      <select
                                        value={age === "" ? "" : age}
                                        onChange={(e) => handleUpdateChildAge(index, childIndex, e.target.value)}
                                        className="w-full pl-3 pr-10 py-2.5 text-sm border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500 bg-white appearance-none cursor-pointer"
                                        required
                                      >
                                        <option value="" disabled>select</option>
                                        {[...Array(18)].map((_, i) => (
                                          <option key={i} value={i}>
                                            {i} {i === 1 ? "year" : "years"}
                                          </option>
                                        ))}
                                      </select>
                                      <div className="absolute right-0 top-0 bottom-0 flex items-center pr-3 pointer-events-none">
                                        <div className="h-4 w-px bg-gray-300 mr-2"></div>
                                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                        </svg>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Actions Footer */}
            <div className="p-4 bg-white border-t border-gray-100 flex justify-between items-center shrink-0">
              <button
                type="button"
                onClick={handleAddRoom}
                disabled={hasUnselectedChildAges}
                title={hasUnselectedChildAges ? "Please select ages for all children first" : ""}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors border ${hasUnselectedChildAges ? 'text-gray-400 border-gray-300 cursor-not-allowed bg-gray-50' : 'text-teal-600 hover:text-teal-700 border-teal-600 hover:bg-teal-50 cursor-pointer'}`}
              >
                + Add Room
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!hasUnselectedChildAges) {
                    setIsRoomDropdownOpen(false);
                  }
                }}
                disabled={hasUnselectedChildAges}
                title={hasUnselectedChildAges ? "Please select ages for all children first" : ""}
                className={`px-8 py-2.5 rounded-lg text-sm font-semibold transition-colors shadow-sm ${hasUnselectedChildAges ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-teal-600 hover:bg-teal-700 text-white cursor-pointer'}`}
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">
          What's your estimated per pax cost?
        </label>
        <div className="flex gap-3">
          <select
            value={formData.currency}
            onChange={(e) => {
              trackEvent("currency_selected", {
                selected_currency: e.target.value,
              });
              setFormData({ ...formData, currency: e.target.value });
            }}
            className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900"
          >
            {currencies.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <input
            type="number"
            onWheel={(e) => e.target.blur()}
            value={formData.budget}
            onChange={(e) => {
              setFormData({
                ...formData,
                budget: e.target.value,
              });
            }}
            onBlur={() => {
              if (formData.budget) {
                trackEvent("budget_entered", {
                  budget: Number(formData.budget),
                  budget_range: getBudgetRange(formData.budget),
                  selected_currency: formData.currency,
                });
              }
            }}
            placeholder="Enter your budget"
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900 placeholder-gray-400"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">
          Any specific preferences?
          <span className="text-gray-400 font-normal ml-2">(Optional)</span>
        </label>
        <textarea
          value={formData.preferences}
          onChange={(e) => {
            setFormData({
              ...formData,
              preferences: e.target.value,
            });
          }}
          onBlur={() => {
            if (formData.preferences.trim()) {
              trackEvent("special_preferences_entered", {
                length: formData.preferences.length,
              });
            }
          }}
          placeholder="E.g., wheelchair accessible places, avoid crowded spots, must visit Eiffel Tower, prefer morning activities..."
          rows={3}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900 placeholder-gray-400 resize-none"
        />
      </div>

      <div className="mt-8 p-4 sm:p-6 bg-gray-50 rounded-xl">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Trip Summary
        </h3>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between gap-2">
            <span className="text-gray-500 flex-shrink-0">From</span>
            <span className="text-gray-900 font-medium text-right">
              {formData.startingLocation || "-"}
            </span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-gray-500 flex-shrink-0">Destinations</span>
            <span className="text-gray-900 font-medium text-right max-w-[60%] break-words">
              {formData.destination || "-"}
            </span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-gray-500 flex-shrink-0">Dates</span>
            <span className="text-gray-900 font-medium text-right">
              {formData.startDate && formData.endDate
                ? `${formatDateDisplay(formData.startDate)} - ${formatDateDisplay(formData.endDate)}`
                : "-"}
            </span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-gray-500 flex-shrink-0">Duration</span>
            <span className="text-gray-900 font-medium text-right">
              {durationDisplay}
            </span>
          </div>

          <div className="flex justify-between gap-2">
            <span className="text-gray-500 flex-shrink-0">Rooms</span>
            <span className="text-gray-900 font-medium text-right">
              {rooms.length}
            </span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-gray-500 flex-shrink-0">Budget</span>
            <span className="text-gray-900 font-medium text-right">
              {formData.budget
                ? `${formData.currency} ${formData.budget}`
                : "-"}
            </span>
          </div>
          <div className="flex justify-between gap-2 border-t border-gray-100 pt-2">
            <span className="text-gray-500 flex-shrink-0">Meal Plan</span>
            <span className="text-gray-900 font-medium text-right">
              {formData.mealPlan
                ? formData.mealPlan === "EP"
                  ? "EP — Room Only"
                  : formData.mealPlan === "CP"
                    ? "CP — Breakfast only"
                    : formData.mealPlan === "MAP"
                      ? "MAP — Breakfast + Dinner"
                      : formData.mealPlan === "AP"
                        ? "AP — Breakfast + Lunch + Dinner"
                        : formData.mealPlan
                : "-"}
            </span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-gray-500 flex-shrink-0">
              Hotel Categories
            </span>
            <span className="text-gray-900 font-medium text-right max-w-[60%] break-words">
              {formData.accommodation && formData.accommodation.length > 0
                ? formData.accommodation.join(", ")
                : "-"}
            </span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-gray-500 flex-shrink-0">Trip Type</span>
            <span className="text-gray-900 font-medium text-right">
              {formData.tripType || "-"}
            </span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-gray-500 flex-shrink-0">
              Include Transport
            </span>
            <span className="text-gray-900 font-medium text-right">
              {formData.includeTransport !== false ? "Yes" : "No"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step3Form;
